import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import AdminSidebar from '../components/AdminSidebar';
import DashboardHeader from '../components/DashboardHeader';
import { useDb } from '../utils/DbContext';
import './Dashboard.css';

function formatDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString();
}

const DatabaseVersion = () => {
  const { dbName } = useDb();
  const [rows, setRows] = useState([]);
  const [loadLoading, setLoadLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [message, setMessage] = useState('');
  const [submitLoading, setSubmitLoading] = useState(false);
  const [status, setStatus] = useState({ type: '', text: '' });

  const load = useCallback(async () => {
    setLoadLoading(true);
    setLoadError('');
    try {
      const res = await api.get('/data/database-version');
      setRows(res.data.rows || []);
    } catch (err) {
      setRows([]);
      setLoadError(
        err.response?.data?.error ||
          err.response?.data?.details ||
          'Failed to load database_version'
      );
    } finally {
      setLoadLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load, dbName]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ type: '', text: '' });
    setSubmitLoading(true);
    try {
      const res = await api.post('/data/database-version', { message });
      const row = res.data?.row;
      setStatus({
        type: 'success',
        text: row
          ? `Appended database_version_id ${row.database_version_id}`
          : 'Version appended',
      });
      setMessage('');
      await load();
    } catch (err) {
      setStatus({
        type: 'error',
        text:
          err.response?.data?.error ||
            err.response?.data?.details ||
            'Failed to append database_version',
      });
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <div className="dashboard">
      <DashboardHeader title="Database version" />

      <div className="dashboard-layout">
        <AdminSidebar />
        <div className="dashboard-content" style={{ maxWidth: 960, margin: '0 auto' }}>
          {status.text && <div className={`message ${status.type}`}>{status.text}</div>}

          <div className="form-section">
            <form onSubmit={handleSubmit} className="insert-form">
              <h2>Append database version</h2>
              <div className="form-group">
                <label htmlFor="database-version-message">
                  Message <span className="required">*</span>
                </label>
                <textarea
                  id="database-version-message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  required
                  rows={6}
                  placeholder="Describe this database version change…"
                />
                <small className="form-hint">
                  Appends one row to <code>public.database_version</code>. <code>date</code> defaults to
                  the server's current timestamp.
                </small>
              </div>
              <button
                type="submit"
                className="submit-button"
                disabled={submitLoading || !message.trim()}
              >
                {submitLoading ? 'Appending…' : 'Append version'}
              </button>
            </form>
          </div>

          <div className="form-section">
            <h2>Existing rows</h2>
            {loadError && <div className="message error">{loadError}</div>}
            {loadLoading ? (
              <p className="overview-meta">Loading…</p>
            ) : rows.length === 0 ? (
              <p className="overview-meta">No rows.</p>
            ) : (
              <div className="overview-table-wrap">
                <table className="overview-table">
                  <thead>
                    <tr>
                      <th>database_version_id</th>
                      <th>date</th>
                      <th>message</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.database_version_id}>
                        <td><code>{r.database_version_id}</code></td>
                        <td>{formatDate(r.date)}</td>
                        <td style={{ whiteSpace: 'pre-wrap' }}>{r.message}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DatabaseVersion;
